import { memo, useState } from 'react'

import { AddCircleOutlineOutlined, GraphicEq } from '@mui/icons-material'
import { Box, Button, Divider, Paper, Stack, Typography } from '@mui/material'
import { useFieldArray, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Dialog } from '@shared/components/MaterialUIEnhancements/Dialog'

import { newId } from '../ids'
import { StreamPickerDialogContent } from '../StreamPickerDialogContent'
import type { ConfigFormValues } from '../types'
import { GlobalAudioRow } from './GlobalAudioRow'

export type PanelProps = {
  repositoryId?: string
}

const PanelRaw = ({ repositoryId }: PanelProps) => {
  const { t } = useTranslation(['common', 'domain'])

  const [pickerOpen, setPickerOpen] = useState(false)

  const { control } = useFormContext<ConfigFormValues>()
  const { fields, append, remove } = useFieldArray({ control, name: 'globalAudioRows', keyName: 'fieldId' })

  return (
    <Paper sx={{ p: 2 }}>
      <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="heading-md">{t('domain:Workspace.Event.ConfigEditor.globalAudio')}</Typography>
        <Button
          size="small"
          startIcon={<AddCircleOutlineOutlined />}
          disabled={!repositoryId}
          onClick={() => setPickerOpen(true)}
        >
          {t('domain:Workspace.Event.ConfigEditor.addGlobalAudio')}
        </Button>
      </Box>
      {!fields.length ? (
        <Stack alignItems="center" spacing={1} sx={{ py: 4, color: 'text.secondary' }}>
          <GraphicEq />
          <Typography variant="heading-base">{t('domain:Workspace.Event.ConfigEditor.noGlobalAudio')}</Typography>
        </Stack>
      ) : (
        <Stack divider={<Divider flexItem />}>
          {fields.map((field, index) => (
            <GlobalAudioRow
              key={field.fieldId}
              index={index}
              repositoryId={repositoryId}
              streamEditable={field.streamEditable}
              onRemove={() => remove(index)}
            />
          ))}
        </Stack>
      )}

      {/* The stream comes first: a global audio without one serializes to a track with no source. */}
      <Dialog
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        title={t('domain:Workspace.Event.ConfigEditor.selectAudioStreamTitle')}
      >
        <StreamPickerDialogContent
          repositoryId={repositoryId}
          kind="audio"
          onSelect={(stream) =>
            append({
              globalAudioId: newId(),
              name: '',
              enabled: true,
              audioTrackId: newId(),
              streamId: stream.id,
              streamEditable: true,
            })
          }
        />
      </Dialog>
    </Paper>
  )
}

export const Panel = memo(PanelRaw)
